import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '@/store/authStore';
import { isBiometricLoginEnabled, hasBiometricCredential } from '@/utils/biometric';

const RESUME_LOCK_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes of background inactivity

/** Handles Android back button, app resume, and biometric lock on resume. */
export function useCapacitorApp() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let backgroundedAt: number | null = null;

    const backSub = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        void CapApp.exitApp();
      }
    });

    const stateSub = CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive) {
        backgroundedAt = Date.now();
      } else {
        // Re-synchronize system status bar appearance upon resume
        try {
          const { syncSystemBars } = await import('@/mobile/services/systemBar.service');
          const { useUiStore } = await import('@/store/uiStore');
          void syncSystemBars(useUiStore.getState().themeMode);
        } catch {
          // Ignore
        }

        if (backgroundedAt && Date.now() - backgroundedAt > RESUME_LOCK_TIMEOUT_MS) {
          try {
            const bioEnabled = await isBiometricLoginEnabled();
            const hasCreds = await hasBiometricCredential();
            if (bioEnabled && hasCreds) {
              const { setBiometricLocked, setAuthState } = useAuthStore.getState();
              setBiometricLocked(true);
              setAuthState('BIOMETRIC_REQUIRED');
            }
          } catch (err) {
            console.warn('[useCapacitorApp] Failed to check biometric lock on resume:', err);
          }
        }
        backgroundedAt = null;
      }
    });


    return () => {
      void backSub.then((h) => h.remove());
      void stateSub.then((h) => h.remove());
    };
  }, []);
}
